import { detectPatterns as rawDetect, runLengthFromIndex } from './patterns';
// Resolution order:
// 1. onTurnStart crew triggers (e.g., Bosun +1, Cursed Compass -1)
// 2. Detect all patterns on the played Plank
// 3. For each pattern: apply base effect, then crew modifiers that key on it
// 4. Apply opponent-reactive crew triggers (their crew watching your plank)
// 5. End-of-turn cleanup (move played cards to discard, schedule forced discards)
//
// All numbers are clamped: doubloons can't go negative below 0
// (anything below = sink), and stealing more than opponent has takes
// only what's available.
export function resolveTurn(state, plank) {
    const next = cloneState(state);
    const player = next.players[next.turnOf];
    const opponent = next.players[other(next.turnOf)];
    // Tactician: auto-arrange plank for max value before pattern detection.
    if (hasCrew(player, 'tactician') && plank.length >= 2) {
        plank = bestPlankPermutation(plank, player);
    }
    const playerStart = player.doubloons;
    const opponentStart = opponent.doubloons;
    const result = {
        patterns: [],
        effects: { earn: 0, steal: 0, forceDiscard: 0, notes: [] },
        patternBreakdown: [],
        crewTriggers: [],
    };
    // 0. Captain passives (set on the state in PvE)
    const captainId = next.captainId;
    if (captainId === 'doubloon') {
        result.effects.earn += 1;
        result.crewTriggers.push({
            crewId: 'captain_doubloon',
            note: 'Captain Doubloon: +1 Treasury',
            earn: 1, steal: 0,
        });
    }
    // 1. Turn-start crew
    applyTurnStart(player, result);
    // 2. Detect patterns
    const patterns = detectPatternsForPlayer(plank, player);
    result.patterns = patterns;
    // 3. Apply each pattern with its crew modifiers — TWO PHASES so X Marks
    // the Spot can target the highest-value patterns AFTER crew bonuses.
    const stowawayActive = crewCount(player, 'stowaway') > 0 && player.doubloons < 15;
    const lastStandActive = crewCount(player, 'last_stand') > 0 && player.doubloons < 8;
    const patternHalfActive = !!player.pendingPatternHalf;
    const doubleAll = !!player.pendingDoubleAllPatterns;
    const pendingBonus = player.pendingPatternBonus ?? 0;
    const records = patterns.map((pat, idx) => {
        const mod = applyPatternCrew(pat, player, plank);
        let earn = pat.baseEarn + mod.earnBonus;
        let steal = pat.baseSteal + mod.stealBonus;
        const noteParts = [...mod.notes];
        if (idx === 0 && stowawayActive) {
            earn += 3;
            noteParts.push('Stowaway: low-hull +3');
        }
        if (lastStandActive && steal > 0) {
            steal = steal * 3;
            noteParts.push('Last Stand: steals tripled');
        }
        return { idx, pat, earn, steal, notes: noteParts };
    });
    // Echo: first pattern fires twice. Duplicate the record at idx 0 so the
    // pattern appears in patternBreakdown twice and stacks all per-pattern
    // bonuses naturally. Rule modifier — does not stack across copies.
    const echoActive = hasCrew(player, 'echo') && records.length > 0;
    if (echoActive) {
        const dup = { ...records[0], idx: records.length, notes: [...records[0].notes, 'Echo: copy'] };
        records.push(dup);
        result.crewTriggers.push({
            crewId: 'echo',
            note: 'Echo: first pattern fires twice',
            earn: 0, steal: 0,
        });
    }
    // Boarding Action edict: same shape as Echo but consumed and only on
    // the actual first pattern (not the Echo copy). Doesn't stack with itself.
    if (player.pendingCopyNextPattern && records.length > 0) {
        const idxOfReal = 0;
        const dup = { ...records[idxOfReal], idx: records.length, notes: [...records[idxOfReal].notes, 'Boarding Action: copy'] };
        records.push(dup);
        result.crewTriggers.push({
            crewId: 'boarding_action',
            note: 'Boarding Action: pattern fires twice',
            earn: 0, steal: 0,
        });
        player.pendingCopyNextPattern = false;
    }
    // Phase 2: pick the top N patterns by total value to receive Marks.
    const marksLeft = player.pendingMarks ?? 0;
    const doubledByMark = new Set();
    if (marksLeft > 0 && records.length > 0) {
        const ranked = [...records].sort((a, b) => (b.earn + b.steal) - (a.earn + a.steal));
        for (let i = 0; i < Math.min(marksLeft, ranked.length); i++) {
            doubledByMark.add(ranked[i].idx);
        }
    }
    // Wet Powder edict: zero out the FIRST pattern fired this turn.
    // Consume the flag so it only hits one pattern.
    let wetPowderConsumed = false;
    // Phase 3: walk in detection order, apply Mark / Lightning / half / doubleAll, accumulate.
    for (const rec of records) {
        let earn = rec.earn;
        let steal = rec.steal;
        const noteParts = [...rec.notes];
        if (player.pendingZeroPattern && !wetPowderConsumed) {
            earn = 0;
            steal = 0;
            noteParts.push('Wet Powder: zeroed');
            wetPowderConsumed = true;
            player.pendingZeroPattern = false;
            result.crewTriggers.push({
                crewId: 'wet_powder',
                note: `Wet Powder zeroed ${rec.pat.label}`,
                earn: 0, steal: 0,
            });
        }
        if (pendingBonus && rec.idx === 0) {
            earn += pendingBonus;
            noteParts.push(`Lightning Strike: +${pendingBonus}`);
            result.crewTriggers.push({
                crewId: 'lightning',
                note: `Lightning Strike: +${pendingBonus} to first pattern`,
                earn: 0, steal: 0,
            });
        }
        if (doubleAll) {
            earn *= 2;
            steal *= 2;
            noteParts.push("Kraken's Embrace ×2");
        }
        if (doubledByMark.has(rec.idx)) {
            earn *= 2;
            steal *= 2;
            noteParts.push('X Marks the Spot: doubled');
            result.crewTriggers.push({
                crewId: 'mark',
                note: `X Marks the Spot: ${rec.pat.label} doubled (best pick)`,
                earn: 0, steal: 0,
            });
        }
        if (rec.idx === 0 && patternHalfActive) {
            earn = Math.floor(earn / 2);
            steal = Math.floor(steal / 2);
            noteParts.push('storm: halved');
            result.crewTriggers.push({
                crewId: 'pattern-half',
                note: 'Storm hits first pattern (halved)',
                earn: 0, steal: 0,
            });
        }
        const note = noteParts.length > 0 ? ` (${noteParts.join(', ')})` : '';
        result.patternBreakdown.push({ pattern: rec.pat, earn, steal, note: note || undefined });
        result.effects.earn += earn;
        result.effects.steal += steal;
        if (rec.pat.forceDiscard)
            result.effects.forceDiscard += rec.pat.forceDiscard;
        if (rec.pat.type === 'sandwich') {
            const bilgeN = crewCount(player, 'bilge_rat');
            if (bilgeN > 0) {
                result.effects.forceDiscard += bilgeN;
                result.crewTriggers.push({
                    crewId: 'bilge_rat',
                    note: bilgeN > 1 ? `Bilge Rat ×${bilgeN}: Sandwich → +${bilgeN} overboard` : 'Bilge Rat: Sandwich → +1 overboard',
                    earn: 0, steal: 0,
                });
            }
        }
        if (rec.pat.type === 'mutiny') {
            const plagueN = crewCount(player, 'plague_rat');
            if (plagueN > 0) {
                result.effects.forceDiscard += 4 * plagueN;
                result.crewTriggers.push({
                    crewId: 'plague_rat',
                    note: `Plague Rat${plagueN > 1 ? ` ×${plagueN}` : ''}: Mutiny → +${4 * plagueN} overboard`,
                    earn: 0, steal: 0,
                });
            }
        }
    }
    if (doubleAll) {
        result.crewTriggers.push({
            crewId: 'kraken-embrace',
            note: "Kraken's Embrace: every pattern doubled",
            earn: 0, steal: 0,
        });
    }
    player.pendingMarks = 0;
    player.pendingPatternHalf = false;
    player.pendingDoubleAllPatterns = false;
    player.pendingPatternBonus = 0;
    // Cook: no patterns → +3 per copy
    const cookN = crewCount(player, 'cook');
    if (patterns.length === 0 && cookN > 0) {
        const gain = 3 * cookN;
        result.effects.earn += gain;
        result.crewTriggers.push({
            crewId: 'cook',
            note: `Cook${cookN > 1 ? ` ×${cookN}` : ''}: no patterns → +${gain}`,
            earn: gain, steal: 0,
        });
    }
    // Surgeon (or Saltbound Surgeon): no patterns → heal at turn end
    const surgN = crewCount(player, 'surgeon');
    const saltSurgN = crewCount(player, 'saltbound_surgeon');
    if (patterns.length === 0 && (surgN > 0 || saltSurgN > 0)) {
        const heal = 2 * surgN + 3 * saltSurgN;
        result.effects.earn += heal;
        const crewId = saltSurgN > 0 && surgN === 0 ? 'saltbound_surgeon' : 'surgeon';
        result.crewTriggers.push({
            crewId,
            note: `Surgeon: no patterns → +${heal} hull`,
            earn: heal, steal: 0,
        });
    }
    // Powder Keg: self-damage per Mutiny
    const kegN = crewCount(player, 'powder_keg');
    const mutinyCount = patterns.filter((p) => p.type === 'mutiny').length;
    if (kegN > 0 && mutinyCount > 0) {
        const dmg = 2 * kegN * mutinyCount;
        result.effects.earn -= dmg;
        result.crewTriggers.push({
            crewId: 'powder_keg',
            note: `Powder Keg: -${dmg} hull from Mutiny${mutinyCount > 1 ? `×${mutinyCount}` : ''}`,
            earn: -dmg, steal: 0,
        });
    }
    // Chronicler: track distinct pattern types seen this match, gain +1 per
    // distinct type (capped at 6) at end of turn.
    const chronN = crewCount(player, 'chronicler');
    if (chronN > 0) {
        const seen = new Set(player.patternsSeenThisMatch ?? []);
        for (const p of patterns)
            seen.add(p.type);
        player.patternsSeenThisMatch = Array.from(seen);
        const distinct = Math.min(seen.size, 5);
        const gain = chronN * distinct;
        if (gain > 0) {
            result.effects.earn += gain;
            result.crewTriggers.push({
                crewId: 'chronicler',
                note: `Chronicler${chronN > 1 ? ` ×${chronN}` : ''}: ${distinct} distinct → +${gain}`,
                earn: gain, steal: 0,
            });
        }
    }
    // Vulture: gain +1 per copy when opponent will lose cards overboard from this turn.
    const vultN = crewCount(player, 'vulture');
    if (vultN > 0 && result.effects.forceDiscard > 0) {
        result.effects.earn += vultN;
        result.crewTriggers.push({
            crewId: 'vulture',
            note: `Vulture${vultN > 1 ? ` ×${vultN}` : ''}: overboard → +${vultN}`,
            earn: vultN, steal: 0,
        });
    }
    // Captain Iron passive: all steals +1
    if (captainId === 'iron' && result.effects.steal > 0) {
        result.effects.steal += 1;
        result.crewTriggers.push({
            crewId: 'captain_iron', note: 'Captain Iron: steals +1',
            earn: 0, steal: 1,
        });
    }
    // Captain Stormbringer passive: +1 per number card (2–10) on the plank
    if (captainId === 'stormbringer') {
        const numCount = plank.filter((c) => c.rank !== 'A' && c.rank !== 'J' && c.rank !== 'Q' && c.rank !== 'K' && c.rank !== '*').length;
        if (numCount > 0) {
            result.effects.earn += numCount;
            result.crewTriggers.push({
                crewId: 'captain_stormbringer',
                note: `Captain Stormbringer: ${numCount} number card${numCount !== 1 ? 's' : ''} → +${numCount}`,
                earn: numCount, steal: 0,
            });
        }
    }
    // Captain Salt passive: <10 hull → all earns +2
    if (captainId === 'salt' && player.doubloons < 10) {
        result.effects.earn += 2;
        result.crewTriggers.push({
            crewId: 'captain_salt', note: 'Captain Salt low-hull: +2 earn',
            earn: 2, steal: 0,
        });
    }
    // Iron Cleaver signature: Mutiny +5 steal per copy
    const ironN = crewCount(player, 'iron_cleaver');
    if (ironN > 0) {
        const mutinyCount = patterns.filter((p) => p.type === 'mutiny').length;
        if (mutinyCount > 0) {
            const bonus = 5 * ironN * mutinyCount;
            result.effects.steal += bonus;
            result.crewTriggers.push({
                crewId: 'iron_cleaver',
                note: `Iron Cleaver: Mutiny +${bonus} steal`,
                earn: 0, steal: bonus,
            });
        }
    }
    // Goldhand signature: per 5 doubloons earned, +2 bonus per copy
    const goldN = crewCount(player, 'goldhand');
    if (goldN > 0 && result.effects.earn >= 5) {
        const ticks = Math.floor(result.effects.earn / 5);
        const bonus = 2 * goldN * ticks;
        result.effects.earn += bonus;
        result.crewTriggers.push({
            crewId: 'goldhand',
            note: `Goldhand: ${ticks}×5🪙 milestone → +${bonus}`,
            earn: bonus, steal: 0,
        });
    }
    // Smuggler: 5+ cards played
    const smN = crewCount(player, 'smuggler');
    if (plank.length >= 5 && smN > 0) {
        const add = 3 * smN;
        result.effects.earn += add;
        result.crewTriggers.push({
            crewId: 'smuggler',
            note: smN > 1 ? `Smuggler ×${smN}: 5 cards → +${add}` : '5 cards played → +3',
            earn: add, steal: 0,
        });
    }
    // Storm Caller: Run played → opp pendingPatternHalf (suppressed by Anchor)
    if (crewCount(player, 'storm_caller') > 0 && patterns.some((p) => p.type === 'run')) {
        if (hasCrew(opponent, 'anchor')) {
            result.crewTriggers.push({
                crewId: 'anchor',
                note: `${opponent.name}'s Anchor: Storm Caller blocked`,
                earn: 0, steal: 0,
            });
        }
        else {
            opponent.pendingPatternHalf = true;
            result.crewTriggers.push({
                crewId: 'storm_caller',
                note: "Storm Caller: opponent's next pattern halved",
                earn: 0, steal: 0,
            });
        }
    }
    // Saltwater: Suit Chain → opp pendingHandSize=5 (suppressed by Anchor)
    if (crewCount(player, 'saltwater') > 0 && patterns.some((p) => p.type === 'suit-chain')) {
        if (hasCrew(opponent, 'anchor')) {
            result.crewTriggers.push({
                crewId: 'anchor',
                note: `${opponent.name}'s Anchor: Saltwater blocked`,
                earn: 0, steal: 0,
            });
        }
        else {
            opponent.pendingHandSize = 5;
            result.crewTriggers.push({
                crewId: 'saltwater',
                note: "Saltwater: opponent's hand drops to 5 next turn",
                earn: 0, steal: 0,
            });
        }
    }
    // Scribe: Suit Chain → +1 extra draw next turn (per copy)
    const scribeN = crewCount(player, 'scribe');
    if (scribeN > 0 && patterns.some((p) => p.type === 'suit-chain')) {
        player.extraDrawNextTurn = (player.extraDrawNextTurn ?? 0) + scribeN;
        result.crewTriggers.push({
            crewId: 'scribe',
            note: `Scribe${scribeN > 1 ? ` ×${scribeN}` : ''}: +${scribeN} draw next turn`,
            earn: 0, steal: 0,
        });
    }
    // 4. Opponent-reactive crew (their crew watching your plank)
    applyOpponentReactive(player, opponent, patterns, result);
    // Apply totals to doubloons
    player.doubloons += result.effects.earn;
    let stealActual;
    if (next.mode === 'pve') {
        // PvE: no real opponent. Steal pays from the Treasury (bottomless).
        stealActual = result.effects.steal;
        player.doubloons += stealActual;
    }
    else {
        let intendedSteal = result.effects.steal;
        // Watchman (on opponent): first time per match they'd take 8+, halve.
        if (intendedSteal >= 8 && hasCrew(opponent, 'watchman') && !opponent.watchmanUsed) {
            const reduced = Math.floor(intendedSteal / 2);
            result.crewTriggers.push({
                crewId: 'watchman',
                note: `${opponent.name}'s Watchman: incoming steal ${intendedSteal} → ${reduced}`,
                earn: 0, steal: 0,
            });
            intendedSteal = reduced;
            opponent.watchmanUsed = true;
        }
        stealActual = Math.min(intendedSteal, Math.max(opponent.doubloons, 0));
        opponent.doubloons -= stealActual;
        player.doubloons += stealActual;
        if (stealActual !== intendedSteal) {
            result.effects.notes.push(`Steal capped at ${stealActual} (opponent had ${stealActual + opponent.doubloons - 0}).`);
        }
    }
    // Marauder: stealActual ≥ 6 → opponent loses N more cards overboard
    const marN = crewCount(player, 'marauder');
    if (marN > 0 && stealActual >= 6) {
        result.effects.forceDiscard += marN;
        result.crewTriggers.push({
            crewId: 'marauder',
            note: `Marauder${marN > 1 ? ` ×${marN}` : ''}: 6+ stolen → +${marN} overboard`,
            earn: 0, steal: 0,
        });
        // Re-fire Vulture on the new forceDiscard (doubloons already applied above)
        if (vultN > 0) {
            result.effects.earn += vultN;
            player.doubloons += vultN;
            result.crewTriggers.push({
                crewId: 'vulture',
                note: `Vulture${vultN > 1 ? ` ×${vultN}` : ''}: Marauder overboard → +${vultN}`,
                earn: vultN, steal: 0,
            });
        }
    }
    // Spy: opponent +2 per copy when player played 5+ cards
    const spyN = crewCount(opponent, 'spy');
    if (plank.length >= 5 && spyN > 0) {
        opponent.doubloons += 2 * spyN;
        result.crewTriggers.push({
            crewId: 'spy',
            note: `${opponent.name}'s Spy${spyN > 1 ? ` ×${spyN}` : ''}: 5 cards → +${2 * spyN}`,
            earn: 0, steal: 0,
        });
    }
    // Tax Collector: opponent +1 per copy when player earned 5+
    const taxN = crewCount(opponent, 'tax_collector');
    if (result.effects.earn >= 5 && taxN > 0) {
        opponent.doubloons += taxN;
        result.crewTriggers.push({
            crewId: 'tax_collector',
            note: `${opponent.name}'s Tax Collector${taxN > 1 ? ` ×${taxN}` : ''} skims +${taxN}`,
            earn: 0, steal: 0,
        });
    }
    // Pillager: 5+ stolen this turn → +3 bonus per copy
    const pillN = crewCount(player, 'pillager');
    if (stealActual >= 5 && pillN > 0) {
        const gain = 3 * pillN;
        player.doubloons += gain;
        result.effects.earn += gain;
        result.crewTriggers.push({
            crewId: 'pillager',
            note: `Pillager${pillN > 1 ? ` ×${pillN}` : ''}: 5+ stolen → +${gain}`,
            earn: gain, steal: 0,
        });
    }
    // Schedule forced discards on opponent
    opponent.pendingDiscards += result.effects.forceDiscard;
    // 5. Move plank cards to player's discard
    for (const card of plank) {
        player.discard.push(card);
        const idx = player.hand.findIndex((c) => c.id === card.id);
        if (idx >= 0)
            player.hand.splice(idx, 1);
    }
    // Log — include before/after doubloons so the math is auditable
    let text = summarizeTurn(player.name, plank, result, stealActual);
    text += ` (${player.name}: 🪙${playerStart}→${player.doubloons}`;
    if (opponentStart !== opponent.doubloons) {
        text += `; ${opponent.name}: 🪙${opponentStart}→${opponent.doubloons}`;
    }
    text += ')';
    next.log.push({
        turn: next.turnNumber,
        player: player.id,
        text,
    });
    // Check win/lose
    evaluateEndConditions(next);
    return { state: next, result };
}
function applyTurnStart(player, result) {
    const bosunN = crewCount(player, 'bosun');
    if (bosunN > 0) {
        result.effects.earn += bosunN;
        result.crewTriggers.push({
            crewId: 'bosun',
            note: bosunN > 1 ? `Bosun ×${bosunN}: +${bosunN} each turn` : 'Bosun: +1 each turn',
            earn: bosunN, steal: 0,
        });
    }
    const compassN = crewCount(player, 'cursed_compass');
    if (compassN > 0) {
        result.effects.earn -= compassN;
        result.crewTriggers.push({
            crewId: 'cursed_compass',
            note: compassN > 1 ? `Cursed Compass ×${compassN}: -${compassN} each turn` : 'Cursed Compass: -1 each turn',
            earn: -compassN, steal: 0,
        });
    }
    // Carpenter: +1 per crew (incl. self), capped at +5 per copy.
    const carpN = crewCount(player, 'carpenter');
    if (carpN > 0) {
        const counted = Math.min(player.crew.length, 5);
        const gain = carpN * counted;
        result.effects.earn += gain;
        result.crewTriggers.push({
            crewId: 'carpenter',
            note: `Carpenter${carpN > 1 ? ` ×${carpN}` : ''}: +${gain} (${counted}/5 crew)`,
            earn: gain, steal: 0,
        });
    }
    // Treasurer: similar to Carpenter but separate stack. Capped at +4 per copy.
    const treasN = crewCount(player, 'treasurer');
    if (treasN > 0) {
        const counted = Math.min(player.crew.length, 4);
        const gain = treasN * counted;
        result.effects.earn += gain;
        result.crewTriggers.push({
            crewId: 'treasurer',
            note: `Treasurer${treasN > 1 ? ` ×${treasN}` : ''}: +${gain} (${counted}/4 crew)`,
            earn: gain, steal: 0,
        });
    }
}
function applyPatternCrew(pat, player, _plank) {
    let earnBonus = 0;
    let stealBonus = 0;
    const notes = [];
    const pmN = crewCount(player, 'powder_monkey');
    if (pat.type === 'sandwich' && pmN > 0) {
        stealBonus += pmN;
        notes.push(pmN > 1 ? `Powder Monkey ×${pmN} +${pmN} steal` : 'Powder Monkey +1 steal');
    }
    const ctN = crewCount(player, 'cutthroat');
    if (pat.type === 'sandwich' && ctN > 0) {
        stealBonus += 2 * ctN;
        notes.push(ctN > 1 ? `Cutthroat ×${ctN} +${2 * ctN} steal` : 'Cutthroat +2 steal');
    }
    const cbN = crewCount(player, 'cabin_boy');
    if (pat.type === 'marriage' && cbN > 0) {
        earnBonus += 2 * cbN;
        notes.push(cbN > 1 ? `Cabin Boy ×${cbN} +${2 * cbN}` : 'Cabin Boy +2');
    }
    const drunkN = crewCount(player, 'drunk_first_mate');
    if (pat.type === 'double' && drunkN > 0) {
        earnBonus += drunkN;
        notes.push(drunkN > 1 ? `Drunk First Mate ×${drunkN} +${drunkN}` : 'Drunk First Mate +1');
    }
    const cooperN = crewCount(player, 'cooper');
    if (pat.type === 'run' && cooperN > 0) {
        const add = pat.cardIndices.length * cooperN;
        earnBonus += add;
        notes.push(cooperN > 1 ? `Cooper ×${cooperN} +${add}` : `Cooper +${add}`);
    }
    const compassN = crewCount(player, 'cursed_compass');
    if (pat.type === 'run' && compassN > 0) {
        // Compounding: each copy doubles the Run's base again.
        // 1 copy = 2×, 2 = 4×, 3 = 8×, capped at 32×.
        const mult = 1 << Math.min(compassN, 5);
        earnBonus += pat.baseEarn * (mult - 1);
        notes.push(compassN > 1
            ? `Cursed Compass ×${compassN}: Run ×${mult}`
            : 'Cursed Compass: Run pays double');
    }
    if (pat.type === 'run' && hasCrew(player, 'cartographer') && pat.cardIndices.length >= 4) {
        earnBonus += pat.baseEarn;
        notes.push('Cartographer: Run4+ doubles base');
    }
    const gunnerN = crewCount(player, 'gunner');
    if (pat.type === 'royalty' && gunnerN > 0) {
        stealBonus += 3 * gunnerN;
        notes.push(gunnerN > 1 ? `Gunner ×${gunnerN} +${3 * gunnerN} steal` : 'Gunner +3 steal');
    }
    if (pat.type === 'mutiny' && hasCrew(player, 'kraken_banner') && player.doubloons < 10) {
        stealBonus += pat.baseSteal;
        notes.push('Kraken Banner: Mutiny doubled');
    }
    // Powder Keg: Mutinies steal +4 per copy. Self-damage handled at apply time.
    const kegN = crewCount(player, 'powder_keg');
    if (pat.type === 'mutiny' && kegN > 0) {
        stealBonus += 4 * kegN;
        notes.push(kegN > 1 ? `Powder Keg ×${kegN} +${4 * kegN} steal` : 'Powder Keg +4 steal');
    }
    // Boarding Hook: every Steal-bearing pattern +1 per copy
    const hookN = crewCount(player, 'boarding_hook');
    if (pat.baseSteal > 0 && hookN > 0) {
        stealBonus += hookN;
        notes.push(hookN > 1 ? `Boarding Hook ×${hookN} +${hookN} steal` : 'Boarding Hook +1 steal');
    }
    return { earnBonus, stealBonus, notes };
}
function applyOpponentReactive(player, opponent, patterns, result) {
    const qmN = crewCount(opponent, 'quartermaster');
    const parrotN = crewCount(opponent, 'parrot');
    const sabN = crewCount(opponent, 'saboteur');
    const mirrorN = crewCount(opponent, 'mirror_mage');
    let mirrorSteal = 0;
    for (const pat of patterns) {
        if (pat.type === 'double' && qmN > 0) {
            opponent.doubloons += qmN;
            result.crewTriggers.push({
                crewId: 'quartermaster',
                note: `${opponent.name}'s Quartermaster${qmN > 1 ? ` ×${qmN}` : ''} reacts: +${qmN}`,
                earn: 0, steal: 0,
            });
        }
        if (pat.type === 'marriage' && parrotN > 0) {
            opponent.doubloons += 2 * parrotN;
            result.crewTriggers.push({
                crewId: 'parrot',
                note: `${opponent.name}'s Parrot${parrotN > 1 ? ` ×${parrotN}` : ''} reacts: +${2 * parrotN}`,
                earn: 0, steal: 0,
            });
        }
        if (pat.type === 'run' && sabN > 0) {
            opponent.doubloons += sabN;
            result.crewTriggers.push({
                crewId: 'saboteur',
                note: `${opponent.name}'s Saboteur${sabN > 1 ? ` ×${sabN}` : ''} reacts: +${sabN}`,
                earn: 0, steal: 0,
            });
        }
        if (pat.type === 'sandwich' && mirrorN > 0) {
            mirrorSteal += mirrorN;
        }
    }
    if (mirrorSteal > 0) {
        const taken = Math.min(mirrorSteal, Math.max(player.doubloons, 0));
        player.doubloons -= taken;
        opponent.doubloons += taken;
        result.crewTriggers.push({
            crewId: 'mirror_mage',
            note: `${opponent.name}'s Mirror Mage steals ${taken}`,
            earn: 0, steal: 0,
        });
    }
    // Spy: opp +2 per copy if PLAYER played 5+ cards.
    const spyN = crewCount(opponent, 'spy');
    if (result.patterns.length >= 0 && spyN > 0) {
        // Use plank length via pattern card indices isn't reliable; we don't
        // have plank here. Mirror handles it server-side; client preview will
        // count via global plank length passed at call site (resolveTurn already
        // applies on plank length below if needed). We use a sentinel here:
        // patterns is fine, but plank length triggers it — handled by caller.
        void spyN;
    }
}
function summarizeTurn(name, plank, result, stealActual) {
    const cards = plank.map((c) => `${c.rank}${suitGlyph(c.suit)}`).join(' ');
    const patternNames = result.patterns.map((p) => p.label).join(', ') || 'no patterns';
    const earn = result.effects.earn;
    const earnText = earn === 0 ? '' : earn > 0 ? `+${earn} earn` : `${earn} earn`;
    const stealText = stealActual > 0 ? `${stealActual} stolen` : '';
    const tail = [earnText, stealText].filter(Boolean).join(', ') || 'no swing';
    const crewNotes = result.crewTriggers.map((t) => t.note).filter(Boolean);
    const breakdown = crewNotes.length > 0 ? ` [${crewNotes.join('; ')}]` : '';
    return `${name} played ${cards} → ${patternNames}. ${tail}.${breakdown}`;
}
function suitGlyph(suit) {
    if (suit === 'spades')
        return '♠';
    if (suit === 'hearts')
        return '♥';
    if (suit === 'diamonds')
        return '♦';
    if (suit === 'clubs')
        return '♣';
    if (suit === '*')
        return '★';
    return '?';
}
function evaluateEndConditions(state) {
    const a = state.players.a;
    const b = state.players.b;
    // PvE: only captain matters. Sink ends the run; plunder via thresholds
    // happens in the lifecycle code, not here.
    if (state.mode === 'pve') {
        if (a.doubloons <= 0) {
            state.status = 'finished';
            state.winner = null;
            state.endReason = 'sink';
        }
        return;
    }
    if (a.doubloons <= 0 && b.doubloons <= 0) {
        state.status = 'finished';
        state.winner = a.doubloons >= b.doubloons ? 'a' : 'b';
        state.endReason = 'sink';
        return;
    }
    if (a.doubloons <= 0) {
        state.status = 'finished';
        state.winner = 'b';
        state.endReason = 'sink';
        return;
    }
    if (b.doubloons <= 0) {
        state.status = 'finished';
        state.winner = 'a';
        state.endReason = 'sink';
        return;
    }
    if (a.doubloons >= state.winThreshold && b.doubloons >= state.winThreshold) {
        state.status = 'finished';
        state.winner = a.doubloons >= b.doubloons ? 'a' : 'b';
        state.endReason = 'plunder';
        return;
    }
    if (a.doubloons >= state.winThreshold) {
        state.status = 'finished';
        state.winner = 'a';
        state.endReason = 'plunder';
        return;
    }
    if (b.doubloons >= state.winThreshold) {
        state.status = 'finished';
        state.winner = 'b';
        state.endReason = 'plunder';
    }
}
export function hasCrew(player, crewId) {
    return crewCount(player, crewId) > 0;
}
export function crewCount(player, crewId) {
    let n = 0;
    for (const c of player.crew)
        if (c.id === crewId)
            n++;
    return n;
}
export function other(p) {
    return p === 'a' ? 'b' : 'a';
}
export function cloneState(state) {
    return JSON.parse(JSON.stringify(state));
}
// Pattern detection that accounts for the current player's Crew modifiers.
export function detectPatternsForPlayer(plank, player) {
    const base = rawDetect(plank);
    const extras = [];
    // Navigator (or Tideborn Navigator signature): 2-card suit chain bonus.
    // Tideborn upgrades the steal from 1 → 2.
    const navHas = hasCrew(player, 'navigator');
    const tidebornHas = hasCrew(player, 'tideborn_navigator');
    if (navHas || tidebornHas) {
        const covered = new Set();
        for (const m of base) {
            if (m.type === 'suit-chain')
                for (const i of m.cardIndices)
                    covered.add(i);
        }
        for (let i = 0; i < plank.length - 1; i++) {
            if (covered.has(i) || covered.has(i + 1))
                continue;
            const a = plank[i].suit, b = plank[i + 1].suit;
            if ((a === b || a === '*' || b === '*') && (a !== '*' || b !== '*')) {
                const anchor = a !== '*' ? a : b;
                extras.push({
                    type: 'suit-chain',
                    cardIndices: [i, i + 1],
                    baseEarn: 0,
                    baseSteal: tidebornHas ? 2 : 1,
                    label: `${anchor} chain (${tidebornHas ? 'Tideborn' : 'Navigator'})`,
                });
                covered.add(i);
                covered.add(i + 1);
            }
        }
    }
    // Helmsman: re-run Run detection with a 1-rank gap budget. Replaces the
    // base Run patterns (which always allow 0 gaps). Cardindices remain
    // contiguous positional slots so Cartographer's "Run of 4+" check works.
    let resultBase = base;
    if (hasCrew(player, 'helmsman')) {
        resultBase = base.filter((p) => p.type !== 'run');
        const consumed = new Set();
        for (let i = 0; i < plank.length; i++) {
            if (consumed.has(i))
                continue;
            const asc = runLengthFromIndex(plank, i, +1, 1);
            const desc = runLengthFromIndex(plank, i, -1, 1);
            const length = Math.max(asc, desc);
            if (length >= 3) {
                const indices = [];
                for (let k = 0; k < length; k++) {
                    indices.push(i + k);
                    consumed.add(i + k);
                }
                extras.push({
                    type: 'run',
                    cardIndices: indices,
                    baseEarn: length,
                    baseSteal: 0,
                    label: `Run of ${length} (Helmsman gap)`,
                });
            }
        }
    }
    return [...resultBase, ...extras];
}
// Tactician helper: try every permutation of the plank, score by raw
// pattern value, return the best ordering. Capped at 5! = 120 perms.
export function bestPlankPermutation(plank, player) {
    const all = permutations(plank);
    let best = plank;
    let bestScore = -1;
    for (const perm of all) {
        const pats = detectPatternsForPlayer(perm, player);
        let s = 0;
        for (const p of pats)
            s += p.baseEarn + p.baseSteal;
        if (s > bestScore) {
            bestScore = s;
            best = perm;
        }
    }
    return best;
}
function permutations(arr) {
    if (arr.length <= 1)
        return [arr.slice()];
    const out = [];
    for (let i = 0; i < arr.length; i++) {
        const rest = arr.slice(0, i).concat(arr.slice(i + 1));
        for (const p of permutations(rest))
            out.push([arr[i], ...p]);
    }
    return out;
}
